import React, { useEffect, useState } from 'react';
import { Card, Form, Button } from 'react-bootstrap';
import { QuestionTable } from '@components/admin/QuestionTable';
import { QuestionBlockEditor } from '@components/admin/QuestionBlockEditor';
import { QuestionImportModal } from '@components/admin/QuestionImportModal';
import { repositoryFactory } from '@data';
import { useAuth } from '@context';
import { getManagedCourses } from '@utils/permissions';

export function QuestionsPage() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [currentModuleId, setCurrentModuleId] = useState(null);
  const [loading, setLoading] = useState(true);

  const repo = repositoryFactory.getCourseRepository();

  // Load all courses on mount
  useEffect(() => {
    const loadCourses = async () => {
      const allCourses = await repo.getAllCourses();
      const scopedCourses = getManagedCourses(user, allCourses);
      setCourses(scopedCourses);
      if (scopedCourses.length > 0) {
        setSelectedCourse(scopedCourses[0]);
      }
      setLoading(false);
    };
    loadCourses();
  }, [user]);

  // Question handlers
  const handleAddQuestion = (moduleId) => {
    setCurrentModuleId(moduleId);
    setEditingQuestion(null);
    setShowQuestionModal(true);
  };

  const handleEditQuestion = (question, moduleId) => {
    setCurrentModuleId(moduleId);
    setEditingQuestion(question);
    setShowQuestionModal(true);
  };

  const handleSaveQuestion = async (questionData) => {
    if (!selectedCourse || !currentModuleId) return;

    const module = selectedCourse.sections.find(s => s.id === currentModuleId);
    if (!module) return;

    const questions = module.questions || [];
    const existingIndex = questions.findIndex(q => q.id === questionData.id);

    if (existingIndex >= 0) {
      questions[existingIndex] = questionData;
    } else {
      questions.push(questionData);
    }

    await repo.upsertSection(selectedCourse.id, { ...module, questions });
    setShowQuestionModal(false);
    setEditingQuestion(null);
    setCurrentModuleId(null);
    
    // Reload the selected course to reflect changes
    const updatedCourse = await repo.getCourseById(selectedCourse.id);
    setSelectedCourse(updatedCourse);
  };

  const handleDeleteQuestion = async (moduleId, questionId) => {
    if (!selectedCourse) return;
    if (window.confirm('Delete this question?')) {
      const module = selectedCourse.sections.find(s => s.id === moduleId);
      if (!module) return;

      const questions = (module.questions || []).filter(q => q.id !== questionId);
      await repo.upsertSection(selectedCourse.id, { ...module, questions });
      
      // Reload the selected course
      const updatedCourse = await repo.getCourseById(selectedCourse.id);
      setSelectedCourse(updatedCourse);
    }
  };

  const handleCourseChange = async (courseId) => {
    const course = courses.find(c => c.id === courseId);
    setSelectedCourse(course);
  };

  const handleImportQuestions = async (importedData) => {
    if (!selectedCourse) return;

    let updatedCourse = { ...selectedCourse };
    const createdSections = new Set();

    for (const item of importedData) {
      // Find or create section
      let section = updatedCourse.sections?.find(s => s.title.toLowerCase() === item.section.toLowerCase());

      if (!section) {
        // Create new section
        section = {
          id: `section-${Date.now()}-${Math.random()}`,
          title: item.section,
          description: '',
          index: updatedCourse.sections?.length || 0,
          examples: [],
          questions: [],
        };
        updatedCourse.sections = updatedCourse.sections || [];
        updatedCourse.sections.push(section);
        createdSections.add(section.id);
      }

      // Build the question object
      const question = {
        id: `question-${Date.now()}-${Math.random()}`,
        type: item.type.toLowerCase(),
        text: item.question,
        correctAnswer: parseCorrectAnswer(item.correctAnswer, item.type),
        explanation: item.explanation || '',
        difficulty: item.difficulty || 'medium',
        tags: item.tags ? item.tags.split(';').map(t => t.trim()).filter(Boolean) : [],
        options: [],
        leftItems: [],
        rightItems: [],
        items: [],
      };

      // Type-specific processing
      if (['multiple-choice', 'multi-select'].includes(question.type)) {
        question.options = item.options.split(';').map(o => o.trim()).filter(Boolean);
      }

      if (question.type === 'matching') {
        question.leftItems = item.leftItems.split(';').map((item, idx) => ({
          id: `left-${idx}`,
          text: item.trim(),
        })).filter(item => item.text);

        question.rightItems = item.rightItems.split(';').map((item, idx) => ({
          id: `right-${idx}`,
          text: item.trim(),
        })).filter(item => item.text);
      }

      if (question.type === 'drag-drop') {
        question.items = item.items.split(';').map((item, idx) => ({
          id: `item-${idx}`,
          text: item.trim(),
        })).filter(item => item.text);
      }

      // Add question to section
      const sectionIndex = updatedCourse.sections.findIndex(s => s.id === section.id);
      if (sectionIndex >= 0) {
        updatedCourse.sections[sectionIndex].questions = updatedCourse.sections[sectionIndex].questions || [];
        updatedCourse.sections[sectionIndex].questions.push(question);
      }
    }

    // Save the updated course
    await repo.saveCourse(updatedCourse);

    // Reload the course
    const reloadedCourse = await repo.getCourseById(updatedCourse.id);
    setSelectedCourse(reloadedCourse);

    // Reload all scoped courses
    const allCourses = await repo.getAllCourses();
    setCourses(getManagedCourses(user, allCourses));

    setShowImportModal(false);
  };

  const parseCorrectAnswer = (answer, type) => {
    if (!answer) return '';

    const typeStr = String(type).toLowerCase();

    if (typeStr === 'multi-select') {
      return answer.split(';').map(a => a.trim()).filter(Boolean);
    }

    if (typeStr === 'matching') {
      // Expected format: "left1:right1;left2:right2"
      const pairs = {};
      answer.split(';').forEach(pair => {
        const [left, right] = pair.split(':').map(s => s.trim());
        if (left && right) pairs[left] = right;
      });
      return pairs;
    }

    if (typeStr === 'drag-drop') {
      return answer.split(';').map(a => a.trim()).filter(Boolean);
    }

    return answer;
  };

  if (loading) {
    return <div className="text-center p-5">Loading courses...</div>;
  }

  if (!selectedCourse && courses.length === 0) {
    return (
      <div>
        <h4 className="mb-4">Question Bank</h4>
        <Card className="text-center p-5">
          <p className="text-muted mb-0">No assigned courses available for question management.</p>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <h4 className="mb-4">Question Bank</h4>

      {/* Course Selector */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Form.Group className="mb-0">
            <Form.Label className="fw-bold">Select a Course</Form.Label>
            <Form.Select
              value={selectedCourse?.id || ''}
              onChange={(e) => handleCourseChange(e.target.value)}
              className="me-3"
            >
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Card.Body>
      </Card>

      {/* Questions Table */}
      {selectedCourse ? (
        <Card className="shadow-sm">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h5>{selectedCourse.title} - Questions</h5>
                <p className="text-muted small">
                  {selectedCourse.sections?.length || 0} modules · {
                    selectedCourse.sections?.reduce((sum, s) => sum + (s.questions?.length || 0), 0) || 0
                  } questions total
                </p>
              </div>
              <Button 
                variant="outline-success" 
                size="sm"
                onClick={() => setShowImportModal(true)}
              >
                📥 Import CSV
              </Button>
            </div>
            <QuestionTable
              course={selectedCourse}
              onEditQuestion={handleEditQuestion}
              onDeleteQuestion={handleDeleteQuestion}
              onAddQuestion={handleAddQuestion}
            />
          </Card.Body>
        </Card>
      ) : (
        <Card className="text-center p-5">
          <p className="text-muted">No courses available</p>
        </Card>
      )}

      {/* Question Modal */}
      <QuestionBlockEditor
        show={showQuestionModal}
        onHide={() => {
          setShowQuestionModal(false);
          setEditingQuestion(null);
          setCurrentModuleId(null);
        }}
        question={editingQuestion}
        onSave={handleSaveQuestion}
      />

      {/* Import Modal */}
      <QuestionImportModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        course={selectedCourse}
        onImportSuccess={handleImportQuestions}
      />
    </div>
  );
}
